import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { DRIVER_ENDPOINT, PAYMENT_ENDPOINT, TRANSACTION_ENDPOINT } from '@/constants/Endpoints'
import { useUserLogin } from '@/stores/userLogin'
import { LOGGER } from '@/util/logger'
import { DONATION_FIELD_MAP } from '@/constants/ShoppingCartConstants'
import { getRouter } from '@/router'
import { useCompletedTransactionStore } from '@/stores/completedTransaction'

const MAX_CART_ITEMS = 95

export const useShoppingCartStore = defineStore('shoppingCart', () => {
  const cartItems = ref([])
  const pendingAdditions = new Set()
  const cartLimitError = ref(false)
  const transactionError = ref('')
  const cartUpdateError = ref('')
  const completedTransactionStore = useCompletedTransactionStore()
  let checkoutResponse = ref(null)
  const issueDate = ref(null)
  const generateUuid = (cartItem) => {
    if (cartItem.serviceType === 'VRCOA') {
      return `${cartItem.serviceType}-${cartItem.titleNumber}-${cartItem.tagNumber}`
    }
    if (cartItem.titleNumber && !cartItem.idNumber) {
      return `${cartItem.serviceType}-${cartItem.titleNumber}`
    }
    return `${cartItem.serviceType}-${cartItem.idNumber || Date.now()}`
  }

  const normalizeDonationFlags = (cartItem) => {
    if (cartItem.type !== 'driver') return cartItem
    const overrides = Object.fromEntries(
      Object.values(DONATION_FIELD_MAP).map((apiField) => [apiField, cartItem[apiField] === true]),
    )
    return { ...cartItem, ...overrides }
  }

  const getCartItemUuids = () =>
    new Set([
      ...cartItems.value.map((entry) => entry.tempTransactionDetails.uuid),
      ...[...pendingAdditions].flat(),
    ])

  const checkCartCapacity = (additionalCount = 1) => {
    cartLimitError.value = false
    if (getCartItemUuids().size + additionalCount <= MAX_CART_ITEMS) return true

    cartLimitError.value = true
    return false
  }

  const updateCartItem = async ({
    transactionID,
    cartItem,
    tempTransactionDetails = {},
    postUpdate = true,
    endpoint = TRANSACTION_ENDPOINT.ADD_TO_CART,
    requestBody,
  }) => {
    const normalizedCartItem = normalizeDonationFlags(cartItem)
    const uuid = generateUuid(normalizedCartItem)
    normalizedCartItem.issueDate = issueDate.value
    if (postUpdate) {
      const isCartUpdate = endpoint === TRANSACTION_ENDPOINT.ADD_TO_CART
      if (isCartUpdate) cartUpdateError.value = ''
      try {
        const response = await axios.post(endpoint, requestBody ?? { transactionID, cartItem: normalizedCartItem })
        if (response.status !== 200) {
          if (!isCartUpdate) return false
          throw new Error('Unable to add this item to your cart. Please try again.')
        }
      } catch (error) {
        if (isCartUpdate) {
          cartUpdateError.value = 'Unable to add this item to your cart. Please try again.'
          LOGGER.logException(error)
        }
        throw error
      }
    }

    const existingIndex = cartItems.value.findIndex((entry) => entry.tempTransactionDetails.uuid === uuid)
    if (existingIndex === -1) {
      cartItems.value.push({
        transactionID,
        cartItem: normalizedCartItem,
        tempTransactionDetails: { ...tempTransactionDetails, uuid },
      })
    } else {
      cartItems.value[existingIndex] = {
        transactionID,
        cartItem: normalizedCartItem,
        tempTransactionDetails: { ...tempTransactionDetails, uuid },
      }
    }
    return true
  }

  const addCartItems = async (entries) => {
    const uuids = entries.map(({ cartItem }) => generateUuid(cartItem))
    const currentUuids = getCartItemUuids()
    const additionalCount = new Set(uuids.filter((uuid) => !currentUuids.has(uuid))).size
    if (!checkCartCapacity(additionalCount)) return false

    pendingAdditions.add(uuids)
    try {
      for (const entry of entries) {
        if (!(await updateCartItem(entry))) return false
      }
      return true
    } finally {
      pendingAdditions.delete(uuids)
    }
  }

  const addItemToCart = (entry) => addCartItems([entry])

  const getCartData = async (transactionID) => {
    const response = await axios.get(TRANSACTION_ENDPOINT.GET_CART_DATA(transactionID), {
      __disableSpinner__: true,
    })
    const data = response.data.data
    const update = (item) => {
      const tempTransactionDetails = {
        addOrganDonor: item.addOrganDonor,
        addVeteransDesignation: item.addVeteransDesignation,
        renewalYears: item.renewalYears,
        feeData: { ...data.feeInformation[`${item.type === 'vehicle' ? item.titleNumber : item.idNumber}_${item.serviceType}`] },
      }
      addItemToCart({ transactionID, cartItem: item, postUpdate: false, tempTransactionDetails })
    }
    data.driverServices?.map((item) => {
      update(item)
    })
    data.vehicleServices?.map((item) => {
      update(item)
    })
    checkoutResponse.value = {
      wid: data.wid,
      orderNumber: data.webOrderNumber,
      paymentTimestamp: data.paymentTimestamp,
      cardNumber: data.cardNumber,
      card: data.card,
      payProcessorStatus: data.payProcessorStatus,
      userEmailAddress: data.userEmailAddress,
    }
    return data.clientDriverDTO
  }

  const removeItemFromCart = async (uuid) => {
    const entry = cartItems.value.find((e) => e.tempTransactionDetails.uuid === uuid)
    if (!entry) return

    await axios.delete(TRANSACTION_ENDPOINT.DELETE_FROM_CART, {
      data: { transactionID: entry.transactionID, cartItem: entry.cartItem },
    })
    cartItems.value = cartItems.value.filter((e) => e.tempTransactionDetails.uuid !== uuid)
  }

  const cancelEdit = async (pendingEditUuid) => {
    if (!pendingEditUuid) return
    await removeItemFromCart(pendingEditUuid)
  }

  const clearCart = () => {
    cartItems.value = []
    checkoutResponse.value = null
  }

  const removeCoaItems = () => {
    cartItems.value = cartItems.value.filter((entry) => {
      return !['DLCOA', 'IDCOA', 'VRCOA'].includes(entry.cartItem.serviceType)
    })
  }

  const getCartItems = () => {
    return cartItems.value
  }

  const hasItems = computed(() => cartItems.value.length > 0)

  const validateIssueDate = async (value) => {
    const userLogin = useUserLogin()
    const transactionID =
      userLogin.loginResponse?.transactionOutputDTO?.transactionID ||
      userLogin.vsLoginResponse?.transactionOutputDTO?.transactionID ||
      null

    if (!transactionID) {
      throw new Error('Cannot validate issue date without a transaction ID')
    }

    const idNumber = userLogin.currentDlNumber || userLogin.currentTitleNumber || null
    if (!idNumber) {
      throw new Error('Cannot validate issue date without an identification number')
    }

    issueDate.value = value
    await axios.post(DRIVER_ENDPOINT.VALIDATE_ISSUE_DATE, {
      transactionID,
      idNumber,
      issueDate: issueDate.value,
    })

    return true
  }

  const checkout = async (userEmail, confirmEmail) => {
    transactionError.value = ''
    const userLogin = useUserLogin()
    const transactionID =
      userLogin.loginResponse?.transactionOutputDTO?.transactionID ??
      userLogin.vsLoginResponse?.transactionOutputDTO?.transactionID ??
      null

    const encyptedForm = await axios.post(PAYMENT_ENDPOINT.CHECKOUT, {
      userEmail: userEmail || null,
      confirmEmail: confirmEmail || null,
      transactionID,
      idNumber: userLogin.currentDlNumber || userLogin.currentTitleNumber,
    })
    document.getElementById('__PostForm')?.remove()
    const paymentFormWrapper = document.createElement('div')
    const data = encyptedForm.data.data
    const paymentStatus = String(data?.status ?? '')
      .trim()
      .toLowerCase()

    if (paymentStatus === 'ok' || paymentStatus === 'success') {
      checkoutResponse.value = { ...data }
      completedTransactionStore.commitFromCart()
      const router = getRouter()
      router.push({ name: 'TransactionComplete' })
      return
    }

    if (data?.url) {
      window.location.href = location.origin + data.url
      return
    }

    paymentFormWrapper.innerHTML = encyptedForm.data.data
    document.getElementsByTagName('body')[0].append(paymentFormWrapper)
    document.getElementById('__PostForm')?.submit()
  }

  const addItemsToCart = async (items) => {
    const userLogin = useUserLogin()
    const transactionID =
      userLogin.loginResponse?.transactionOutputDTO?.transactionID ??
      userLogin.vsLoginResponse?.transactionOutputDTO?.transactionID ??
      null

    return addCartItems(items.map((item) => ({ ...item, transactionID })))
  }

  const getCheckoutStatus = async () => {
    const userLogin = useUserLogin()
    const { transactionId, idNumber } = await userLogin.refreshToken()

    if (!transactionId) {
      transactionError.value = 'Transaction details are missing.'
      return
    }
    const id = setInterval(async () => {
      const response = await axios.post(
        PAYMENT_ENDPOINT.CHECKOUT_STATUS,
        {
          transactionID: transactionId,
          idNumber: idNumber,
        },
        {
          __disableSpinner__: true,
        },
      )
      const data = response.data.data
      const router = getRouter()
      if (data.paymentStatus === 'COMPLETED_WITH_ERROR') {
        clearInterval(id)
        const thinDriver = await getCartData(transactionId)
        userLogin.setLoginResponses(thinDriver, transactionId)
        router.push({ name: 'ShoppingCart' })
      }

      if (data.paymentStatus === 'COMPLETED') {
        clearInterval(id)
        const thinDriver = await getCartData(transactionId)
        userLogin.setLoginResponses(thinDriver, transactionId)
        completedTransactionStore.commitFromCart()
        router.push({ name: 'TransactionComplete' })
      }
    }, 1500)
  }

  const downloadAndPrint = async (transactionID) => {
    if (!transactionID) throw new Error('Transaction ID is required')

    try {
      const response = await axios.get(TRANSACTION_ENDPOINT.DOWNLOAD_AND_PRINT(transactionID), {
        responseType: 'blob',
      })

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = globalThis.URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (err) {
      LOGGER.logException(err)
      throw err
    }
  }

  /**
   * Set the id Number as the current one. Ex: When Add another service for this record is clicked and multiple IDs are in the cart, this will set the idNumber that is clicked on.
   * @param {*} idNumber
   */
  const setCurrentIDNumber = (idNumber) => {
    const userLogin = useUserLogin()
    userLogin.currentDlNumber = idNumber
  }

  return {
    cartItems,
    cartUpdateError,
    addItemToCart,
    addItemsToCart,
    checkCartCapacity,
    cartLimitError,
    removeItemFromCart,
    cancelEdit,
    clearCart,
    removeCoaItems,
    getCartItems,
    hasItems,
    checkout,
    downloadAndPrint,
    checkoutResponse,
    setCurrentIDNumber,
    getCheckoutStatus,
    getCartData,
    validateIssueDate
  }
})
